/**
 * useExperimentVariant — public A/B variant assignment hook.
 *
 * Given a `location` string (e.g., "learn_article", "tools_landing"), fetches
 * the active experiments at that location, deterministically assigns the
 * visitor a variant via the backend hash, fires an `exposed` event once per
 * session, and emits a PostHog event so analytics see the same variant_id.
 *
 * Returns a map keyed by experiment_id with the chosen variant + its config.
 *
 * Cost-safe: no AI, no expensive joins. Result is cached in sessionStorage so
 * repeated mounts within a tab don't refetch.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

function anonymousId() {
    try {
        let id = localStorage.getItem("bl_anonymous_id");
        if (!id) {
            id = `anon_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
            localStorage.setItem("bl_anonymous_id", id);
        }
        return id;
    } catch {
        return `anon_${Math.random().toString(36).slice(2)}`;
    }
}

function fireExposed(experimentId, variantId) {
    const key = `exp_exposed_${experimentId}`;
    try {
        if (sessionStorage.getItem(key) === variantId) return;
        sessionStorage.setItem(key, variantId);
    } catch {
        /* no-op */
    }
    const anon = anonymousId();
    // Internal ab_events ingestion
    api.post("/experiments/event", {
        experiment_id: experimentId,
        variant_id: variantId,
        event_name: "exposed",
        anonymous_id: anon,
    }).catch(() => {});
    // PostHog mirror (only if it exists)
    if (typeof window !== "undefined" && window.posthog?.capture) {
        try {
            window.posthog.capture("experiment_exposed", {
                experiment_id: experimentId,
                variant_id: variantId,
            });
        } catch {
            /* no-op */
        }
    }
}

export function fireExperimentEvent(experimentId, variantId, eventName, value, metadata) {
    const anon = anonymousId();
    api.post("/experiments/event", {
        experiment_id: experimentId,
        variant_id: variantId,
        event_name: eventName,
        event_value: value ?? null,
        anonymous_id: anon,
        metadata: metadata || null,
    }).catch(() => {});
    if (typeof window !== "undefined" && window.posthog?.capture) {
        try {
            window.posthog.capture(`experiment_${eventName}`, {
                experiment_id: experimentId,
                variant_id: variantId,
                event_value: value ?? null,
                ...(metadata || {}),
            });
        } catch {
            /* no-op */
        }
    }
}

export default function useExperimentVariant(location) {
    const [experiments, setExperiments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        if (!location) {
            setLoading(false);
            return;
        }
        // Session cache so re-mounts don't refetch.
        const cacheKey = `exp_cache_${location}`;
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                const parsed = JSON.parse(cached);
                setExperiments(parsed || []);
                setLoading(false);
                // Still fire exposure events
                (parsed || []).forEach((e) =>
                    fireExposed(e.experiment_id, e.variant_id),
                );
                return;
            }
        } catch {
            /* no-op */
        }
        (async () => {
            try {
                const anon = anonymousId();
                const r = await api.get("/experiments/active", {
                    params: { location, anonymous_id: anon },
                });
                if (!alive) return;
                const list = r.data?.experiments || [];
                setExperiments(list);
                try {
                    sessionStorage.setItem(cacheKey, JSON.stringify(list));
                } catch {
                    /* no-op */
                }
                list.forEach((e) => fireExposed(e.experiment_id, e.variant_id));
            } catch {
                if (alive) setExperiments([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [location]);

    return { experiments, loading };
}
